import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Eye, 
  ShoppingCart, 
  DollarSign, 
  TrendingUp, 
  Package,
  Save,
  Facebook,
  ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsManagement = ({ token }) => {
  const [trackingConfig, setTrackingConfig] = useState({
    facebook_pixel_id: '',
    tiktok_pixel_id: '',
    google_analytics_id: ''
  });
  const [productAnalytics, setProductAnalytics] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, analyticsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/tracking-pixels-config`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/products`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTrackingConfig(configRes.data);
      setProductAnalytics(analyticsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const saveTrackingConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tracking-pixels-config`, trackingConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tracking configuration saved!');
    } catch (error) {
      toast.error('Failed to save tracking configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Tracking Pixel Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Tracking Pixels
          </CardTitle>
          <CardDescription>
            Configure Facebook Pixel and TikTok Pixel for ad tracking and conversion optimization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="facebook_pixel" className="flex items-center gap-2">
                <Facebook className="h-4 w-4 text-blue-600" />
                Facebook Pixel ID
              </Label>
              <Input
                id="facebook_pixel"
                placeholder="e.g., 1234567890123456"
                value={trackingConfig.facebook_pixel_id || ''}
                onChange={(e) => setTrackingConfig({...trackingConfig, facebook_pixel_id: e.target.value})}
              />
              <p className="text-xs text-gray-500">
                Find your Pixel ID in Facebook Events Manager
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tiktok_pixel" className="flex items-center gap-2">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-.88-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
                TikTok Pixel ID
              </Label>
              <Input
                id="tiktok_pixel"
                placeholder="e.g., CXXXXXXXXXXXXXXXXX"
                value={trackingConfig.tiktok_pixel_id || ''}
                onChange={(e) => setTrackingConfig({...trackingConfig, tiktok_pixel_id: e.target.value})}
              />
              <p className="text-xs text-gray-500">
                Find your Pixel ID in TikTok Ads Manager
              </p>
            </div>
          </div>
          <Button onClick={saveTrackingConfig} disabled={saving} className="mt-4">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Tracking Config'}
          </Button>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Views</p>
                  <p className="text-2xl font-bold">{summary.total_views.toLocaleString()}</p>
                </div>
                <Eye className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Cart Adds</p>
                  <p className="text-2xl font-bold">{summary.total_cart_adds.toLocaleString()}</p>
                </div>
                <ShoppingCart className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-2xl font-bold">{summary.total_orders.toLocaleString()}</p>
                </div>
                <Package className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-2xl font-bold">${summary.total_revenue.toLocaleString()}</p>
                </div>
                <DollarSign className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Products */}
      {summary?.top_products?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Top Selling Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {summary.top_products.map((product, index) => (
                <div key={product.product_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium">{product.name}</p>
                      <p className="text-sm text-gray-500">{product.purchases} purchases</p>
                    </div>
                  </div>
                  <p className="font-bold text-green-600">${product.revenue.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product Analytics Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Product Performance
          </CardTitle>
          <CardDescription>
            Detailed analytics for each product - views, cart adds, purchases, and conversion rates
          </CardDescription>
        </CardHeader>
        <CardContent>
          {productAnalytics.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No product analytics data yet.</p>
              <p className="text-sm">Data will appear as customers view and purchase products.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Product</th>
                    <th className="text-center py-3 px-2">Views</th>
                    <th className="text-center py-3 px-2">Cart Adds</th>
                    <th className="text-center py-3 px-2">Purchases</th>
                    <th className="text-center py-3 px-2">Revenue</th>
                    <th className="text-center py-3 px-2">Conv. Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {productAnalytics.map((product) => (
                    <tr key={product.product_id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          {product.image_url && (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-10 h-10 object-cover rounded"
                            />
                          )}
                          <div>
                            <p className="font-medium truncate max-w-[200px]">{product.name}</p>
                            <p className="text-xs text-gray-500">{product.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="flex items-center justify-center gap-1">
                          <Eye className="h-3 w-3 text-blue-500" />
                          {product.views.toLocaleString()}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="flex items-center justify-center gap-1">
                          <ShoppingCart className="h-3 w-3 text-orange-500" />
                          {product.cart_adds.toLocaleString()}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="flex items-center justify-center gap-1">
                          <Package className="h-3 w-3 text-green-500" />
                          {product.purchases.toLocaleString()}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2 font-medium text-green-600">
                        ${product.revenue.toLocaleString()}
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          product.conversion_rate > 5 ? 'bg-green-100 text-green-800' :
                          product.conversion_rate > 2 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {product.conversion_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsManagement;
